import {useState, useCallback} from 'react';

/**
 * Custom hook to manage the loading, data, and error states of a promise-based function.
 *
 * @template TArgs The types of arguments the async function accepts.
 * @template TResult The type of the data the async function resolves to.
 *
 * @returns {
 *   loading: boolean; - A boolean indicating whether the promise is in progress.
 *   data: TResult | null; - The resolved data from the promise, or `null` if not loaded yet.
 *   error: Error | null; - Any error that occurred during the async operation, or `null` if no error.
 *   execute: (fn: (...args: TArgs) => Promise<TResult>, ...args: TArgs) => Promise<TResult>; - A function to trigger the async operation.
 * }
 */
const useAsyncState = <TArgs extends any[], TResult>() => {
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState<TResult | null>(null);
    const [error, setError] = useState<Error | null>(null);

    /**
     * Executes the given async function and manages the loading, success, and error states.
     *
     * @param promiseFn - The async function to be executed.
     * @param args - Arguments to be passed to the async function.
     * @returns A promise that resolves with the result of the function or rejects with an error.
     */
    const execute = useCallback(
        async (promiseFn: (...args: TArgs) => Promise<TResult>, ...args: TArgs): Promise<TResult> => {
            setLoading(true);
            setError(null);
            return promiseFn(...args).then((res) => {
                setData(res);
                return res; // Return the result to allow `.then` chaining
            }).catch((error) => {
                setData(null);
                setError(error instanceof Error ? error : new Error('An unknown error occurred'));
                throw error; // Rethrow the error to allow `.catch` chaining
            }).finally(() => setLoading(false))
        },
        [] // No dependencies
    );

    // Return the state and the execute function
    return {loading, data, error, execute};
};

export default useAsyncState;
